import React, { useMemo, useState } from 'react';
import { mockLoans } from '../data/mockLoans';
import { MonthSelector } from './MonthSelector';
import { SummaryBar } from './SummaryBar';
import { LoansTable } from './LoansTable';
import { LayoutDashboard } from 'lucide-react';
type TabType = 'flat-fee' | 'regular' | 'all';
export function FundedLoansDashboard() {
  const [selectedMonth, setSelectedMonth] = useState('October 2023');
  const [activeTab, setActiveTab] = useState<TabType>('flat-fee');
  // Filter loans by model
  const flatFeeLoans = useMemo(() => mockLoans.filter(loan => loan.paymentModel === 'Flat Fee').sort((a, b) => new Date(b.fundedDate).getTime() - new Date(a.fundedDate).getTime()), []);
  const regularLoans = useMemo(() => mockLoans.filter(loan => loan.paymentModel === 'Regular').sort((a, b) => new Date(b.fundedDate).getTime() - new Date(a.fundedDate).getTime()), []);
  const allLoans = useMemo(() => [...mockLoans].sort((a, b) => new Date(b.fundedDate).getTime() - new Date(a.fundedDate).getTime()), []);
  // Calculate stats
  const calculateStats = (loans: typeof mockLoans) => {
    const count = loans.length;
    const volume = loans.reduce((sum, loan) => sum + loan.loanAmount, 0);
    const commissions = loans.reduce((sum, loan) => sum + loan.totalCommissions, 0);
    const avgCommission = count > 0 ? commissions / count : 0;
    return {
      count,
      volume,
      commissions,
      avgCommission
    };
  };
  const flatFeeStats = calculateStats(flatFeeLoans);
  const regularStats = calculateStats(regularLoans);
  const totalVolume = flatFeeStats.volume + regularStats.volume;
  const totalCommissions = flatFeeStats.commissions + regularStats.commissions;
  const totalLoans = flatFeeStats.count + regularStats.count;
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  // Determine which loans to show based on active tab
  const displayedLoans = activeTab === 'flat-fee' ? flatFeeLoans : activeTab === 'regular' ? regularLoans : allLoans;
  return <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-slate-900" data-id="element-1">
      <div className="max-w-7xl mx-auto" data-id="element-2">
        {/* Header Section */}
        <header className="flex flex-col md:flex-row md:items-start justify-between mb-10 gap-6" data-id="element-3">
          <div className="flex-1" data-id="element-4">
            <div className="flex items-center gap-3 mb-2" data-id="element-5">
              <div className="bg-blue-600 p-2 rounded-lg shadow-sm" data-id="element-6">
                <LayoutDashboard className="w-6 h-6 text-white" data-id="element-7" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-8">
                Funded Loans
              </h1>
              <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2.5 py-1 rounded-full border border-slate-200 ml-2" data-id="element-9">
                {totalLoans} Total
              </span>
            </div>
            <p className="text-slate-500 text-sm md:text-base max-w-xl" data-id="element-10">
              Track funded loans, monitor commission payouts, and analyze
              performance by compensation model.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4" data-id="element-11">
            <MonthSelector selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} data-id="element-12" />

            <div className="flex bg-white rounded-lg border border-slate-200 shadow-sm divide-x divide-slate-100 h-[46px]" data-id="element-13">
              <div className="px-5 flex flex-col justify-center" data-id="element-14">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-15">
                  Total Funded
                </div>
                <div className="text-sm font-bold text-slate-900 leading-none" data-id="element-16">
                  {formatCurrency(totalVolume)}
                </div>
              </div>
              <div className="px-5 flex flex-col justify-center bg-slate-50/50" data-id="element-17">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-0.5" data-id="element-18">
                  Total Paid
                </div>
                <div className="text-sm font-bold text-emerald-600 leading-none" data-id="element-19">
                  {formatCurrency(totalCommissions)}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Summary Stats */}
        <SummaryBar flatFeeStats={flatFeeStats} regularStats={regularStats} data-id="element-20" />

        {/* Unified Card Container for Tabs and Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-21">
          {/* Tabbed Interface */}
          <div className="border-b border-slate-200 px-6 bg-slate-50/30" data-id="element-22">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs" role="tablist" data-id="element-23">
              <button onClick={() => setActiveTab('flat-fee')} className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-t
                  ${activeTab === 'flat-fee' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}
                `} aria-selected={activeTab === 'flat-fee'} role="tab" data-id="element-24">
                Flat Fee Model
                <span className={`ml-3 py-0.5 px-2.5 rounded-full text-xs font-bold transition-colors duration-200 ${activeTab === 'flat-fee' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-25">
                  {flatFeeStats.count}
                </span>
              </button>

              <button onClick={() => setActiveTab('regular')} className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 rounded-t
                  ${activeTab === 'regular' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}
                `} aria-selected={activeTab === 'regular'} role="tab" data-id="element-26">
                Regular Model
                <span className={`ml-3 py-0.5 px-2.5 rounded-full text-xs font-bold transition-colors duration-200 ${activeTab === 'regular' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-27">
                  {regularStats.count}
                </span>
              </button>

              <button onClick={() => setActiveTab('all')} className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-t
                  ${activeTab === 'all' ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}
                `} aria-selected={activeTab === 'all'} role="tab" data-id="element-28">
                All Loans
                <span className={`ml-3 py-0.5 px-2.5 rounded-full text-xs font-bold transition-colors duration-200 ${activeTab === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-29">
                  {totalLoans}
                </span>
              </button>
            </nav>
          </div>

          {/* Data Table */}
          <div role="tabpanel" data-id="element-30">
            <LoansTable loans={displayedLoans} showModelColumn={activeTab === 'all'} data-id="element-31" />
          </div>
        </div>
      </div>
    </div>;
}